import { Badge, Button, Card, Group, Menu, SegmentedControl, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { IconAlertTriangle, IconArrowRight, IconChevronDown, IconRefresh } from '@tabler/icons-react';
import { useEffect, useMemo, useState, type FC } from 'react';

import { formatCount } from '@utils/currency';
import { getRebalanceHealth, getRebalancePlans } from '@api/rebalance';
import type { NodeHealthSnapshot, RebalancePlan, RebalanceScope, WarehouseHealthSnapshot, WarehouseHealthResponse } from '@api/rebalance';
import DataSheet, { type DataSheetColumn } from '@components/ui/DataSheet';
import styles from './RebalanceOverview.module.css';

interface Props {
    onRunRebalance: (scope: RebalanceScope) => void;
    onSelectPlan: (plan: RebalancePlan) => void;
}

/** One grid row at either level. Node rows carry their members as sub-rows. */
type HealthRow = WarehouseHealthSnapshot & {
    rowKey: string;
    kind: 'node' | 'warehouse' | 'singleton';
    subRows?: HealthRow[];
    memberCount?: number;
    nodeIdForRun?: string | null;
};

const statusColor = (status: WarehouseHealthSnapshot['status']) => {
    if (status === 'SURPLUS') return 'red';
    if (status === 'DEFICIT') return 'yellow';
    return 'green';
};

const scopeLabel = (plan: RebalancePlan): string => {
    const scope = plan.parameters?.scope;
    if (!scope || scope.level === 'WAREHOUSE') return 'Warehouses';
    if (scope.level === 'NETWORK') return 'Network';
    return `Within ${scope.nodeName ?? 'node'}`;
};

const RebalanceOverview: FC<Props> = ({ onRunRebalance, onSelectPlan }) => {
    const [health, setHealth] = useState<WarehouseHealthSnapshot[]>([]);
    const [nodes, setNodes] = useState<NodeHealthSnapshot[]>([]);
    const [summary, setSummary] = useState({ surplus: 0, deficit: 0, balanced: 0, total: 0 });
    const [recentPlans, setRecentPlans] = useState<RebalancePlan[]>([]);
    const [loading, setLoading] = useState(true);
    const [level, setLevel] = useState<'node' | 'warehouse'>('node');

    const refresh = () => {
        setLoading(true);
        Promise.all([
            getRebalanceHealth().catch(() => ({ warehouses: [], nodes: [], summary: { surplus: 0, deficit: 0, balanced: 0, total: 0 }, computedAt: '' } as WarehouseHealthResponse)),
            getRebalancePlans(20, 0).catch(() => ({ data: [], total: 0 })),
        ]).then(([healthData, plansData]) => {
            setHealth(healthData.warehouses);
            setNodes(healthData.nodes ?? []);
            setSummary(healthData.summary);
            const draftPlans = plansData.data
                .filter(p => p.status === 'DRAFT')
                .sort((a, b) => new Date(b.planDate).getTime() - new Date(a.planDate).getTime())
                .slice(0, 5);
            setRecentPlans(draftPlans);
            setLoading(false);
        });
    };

    useEffect(() => { refresh(); }, []);

    const hasNodes = nodes.some(n => !n.isSingleton);
    const showNodeLevel = hasNodes && level === 'node';

    const rows = useMemo<HealthRow[]>(() => {
        if (!showNodeLevel) {
            return health.map(w => ({ ...w, rowKey: w.warehouseId, kind: 'warehouse' as const }));
        }
        const byId = new Map(health.map(w => [w.warehouseId, w]));
        return nodes.map(n => ({
            ...n,
            rowKey: n.nodeKey,
            kind: n.isSingleton ? ('singleton' as const) : ('node' as const),
            memberCount: n.memberWarehouseIds.length,
            nodeIdForRun: n.isSingleton ? null : n.nodeKey,
            subRows: n.isSingleton
                ? undefined
                : n.memberWarehouseIds
                    .map(id => byId.get(id))
                    .filter((w): w is WarehouseHealthSnapshot => Boolean(w))
                    .map(w => ({ ...w, rowKey: `${n.nodeKey}:${w.warehouseId}`, kind: 'warehouse' as const })),
        }));
    }, [health, nodes, showNodeLevel]);

    const nodeSummary = useMemo(() => {
        const real = nodes.filter(n => !n.isSingleton);
        return {
            surplus: real.filter(n => n.status === 'SURPLUS').length,
            deficit: real.filter(n => n.status === 'DEFICIT').length,
            balanced: real.filter(n => n.status === 'OK').length,
        };
    }, [nodes]);

    const columns: DataSheetColumn<HealthRow>[] = [
        {
            key: 'warehouseName',
            header: showNodeLevel ? 'Node / warehouse' : 'Warehouse',
            width: 240,
            sticky: true,
            render: (_, row) => (
                <Group gap={6} wrap="nowrap">
                    {row.kind === 'node' && <Badge size="xs" variant="light" color="indigo" className={styles.nodeBadge}>NODE</Badge>}
                    <Text size="sm" fw={row.kind === 'node' ? 600 : 500} truncate>
                        {row.warehouseName ?? row.warehouseId}
                    </Text>
                    {row.kind === 'node' && <Text size="xs" c="dimmed">· {row.memberCount} wh</Text>}
                    {row.kind === 'singleton' && <Text size="xs" c="dimmed">· alone</Text>}
                </Group>
            ),
        },
        { key: 'currentStockUnits', header: 'Stock (units)', width: 110, align: 'right', render: (v) => formatCount(v) },
        { key: 'inTransitUnits', header: 'In-Transit', width: 100, align: 'right', render: (v) => formatCount((v ?? 0)) },
        { key: 'avgDailyDemand', header: 'Demand/day', width: 110, align: 'right', render: (v) => v.toFixed(1) },
        { key: 'currentDoh', header: 'DOH', width: 70, align: 'right', render: (v) => v >= 999 ? '—' : `${Math.floor(v)}d` },
        { key: 'targetDoh', header: 'Target', width: 70, align: 'right', render: (v) => `${v}d` },
        {
            key: 'surplusDeficitDays',
            header: 'Vs Target',
            width: 110,
            align: 'right',
            render: (v: number, row) => {
                if (row.avgDailyDemand === 0) return <Text size="xs" c="dimmed">—</Text>;
                const color = v > 0 ? 'red' : v < 0 ? 'yellow.7' : 'green';
                const sign = v > 0 ? '+' : '';
                return <Text size="xs" fw={600} c={color}>{sign}{v}d</Text>;
            },
        },
        {
            key: 'status',
            header: 'Status',
            width: 90,
            align: 'center',
            render: (v: WarehouseHealthSnapshot['status']) => (
                <Badge color={statusColor(v)} variant="light" size="sm">{v}</Badge>
            ),
        },
        ...(showNodeLevel ? [{
            key: '_run',
            header: '',
            width: 150,
            render: (_: unknown, row: HealthRow) => row.kind === 'node' ? (
                <Button
                    size="compact-xs"
                    variant="subtle"
                    color="violet"
                    onClick={() => onRunRebalance({ level: 'NODE', nodeId: row.nodeIdForRun, nodeName: row.warehouseName ?? null })}
                >
                    Rebalance within
                </Button>
            ) : null,
        } as DataSheetColumn<HealthRow>] : []),
    ];

    const { surplus, deficit, balanced } = summary;

    return (
        <Stack gap="lg">
            <Group justify="space-between">
                <Group gap="sm">
                    <Title order={5}>{hasNodes ? 'Network Health' : 'Warehouse Health'}</Title>
                    {hasNodes && (
                        <SegmentedControl
                            size="xs"
                            value={level}
                            onChange={v => setLevel(v as 'node' | 'warehouse')}
                            data={[{ label: 'By node', value: 'node' }, { label: 'By warehouse', value: 'warehouse' }]}
                        />
                    )}
                </Group>
                <Group gap="xs">
                    <Button variant="subtle" size="xs" fw={500} leftSection={<IconRefresh size={14} />} onClick={refresh} loading={loading}>
                        Refresh
                    </Button>
                    {hasNodes ? (
                        <Menu shadow="md" width={240} position="bottom-end">
                            <Menu.Target>
                                <Button variant="action" size="sm" radius="md" rightSection={<IconChevronDown size={16} />}>
                                    Run Rebalance
                                </Button>
                            </Menu.Target>
                            <Menu.Dropdown>
                                <Menu.Item onClick={() => onRunRebalance({ level: 'NETWORK' })}>Across network</Menu.Item>
                                <Menu.Label>Within a node</Menu.Label>
                                {nodes.filter(n => !n.isSingleton).map(n => (
                                    <Menu.Item key={n.nodeKey} onClick={() => onRunRebalance({ level: 'NODE', nodeId: n.nodeKey, nodeName: n.warehouseName ?? null })}>
                                        {n.warehouseName ?? n.nodeKey}
                                    </Menu.Item>
                                ))}
                                <Menu.Divider />
                                <Menu.Item onClick={() => onRunRebalance({ level: 'WAREHOUSE' })}>Every warehouse, ignoring nodes</Menu.Item>
                            </Menu.Dropdown>
                        </Menu>
                    ) : (
                        <Button variant="action" size="sm" radius="md" rightSection={<IconArrowRight size={16} />} onClick={() => onRunRebalance({ level: 'WAREHOUSE' })}>
                            Run Rebalance
                        </Button>
                    )}
                </Group>
            </Group>

            <SimpleGrid cols={3}>
                <Card withBorder radius="md" p="md">
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Surplus</Text>
                    <Text size="xl" fw={700} c="red">{showNodeLevel ? nodeSummary.surplus : surplus}</Text>
                    <Text size="xs" c="dimmed">{showNodeLevel ? 'nodes' : 'warehouses'} overstocked</Text>
                </Card>
                <Card withBorder radius="md" p="md">
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Deficit</Text>
                    <Text size="xl" fw={700} c="yellow.7">{showNodeLevel ? nodeSummary.deficit : deficit}</Text>
                    <Text size="xs" c="dimmed">{showNodeLevel ? 'nodes' : 'warehouses'} understocked</Text>
                </Card>
                <Card withBorder radius="md" p="md">
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Balanced</Text>
                    <Text size="xl" fw={700} c="green">{showNodeLevel ? nodeSummary.balanced : balanced}</Text>
                    <Text size="xs" c="dimmed">{showNodeLevel ? 'nodes' : 'warehouses'} on target</Text>
                </Card>
            </SimpleGrid>

            <DataSheet
                data={rows}
                columns={columns}
                isLoading={loading}
                maxHeight="auto"
                getRowKey={(row) => row.rowKey}
                getSubRows={showNodeLevel ? (row => row.subRows) : undefined}
                getRowClassName={row => row.kind === 'node' ? styles.nodeRow : ''}
                emptyState={
                    <Stack align="center" gap="xs" p="xl">
                        <IconAlertTriangle size={32} color="gray" />
                        <Text c="dimmed" size="sm">No warehouse health data available.</Text>
                    </Stack>
                }
            />

            <Title order={5}>Recent Plans</Title>
            {recentPlans.length === 0 ? (
                <Text size="sm" c="dimmed">No draft plans yet. Run your first rebalance to get started.</Text>
            ) : (
                <Stack gap="xs">
                    {recentPlans.map(plan => (
                        <Card key={plan.id} withBorder radius="md" p="sm" className={styles.planCard} onClick={() => onSelectPlan(plan)}>
                            <Group justify="space-between">
                                <div>
                                    <Text fw={500} size="sm">{plan.name}</Text>
                                    <Text size="xs" c="dimmed">
                                        {new Date(plan.planDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} ·{' '}
                                        {scopeLabel(plan)} ·{' '}
                                        {plan.recommendedTransfers?.length ?? 0} transfers ·{' '}
                                        {plan.stnIds?.length ?? 0} STNs created
                                    </Text>
                                </div>
                                <Group gap="xs">
                                    <Badge color="gray" variant="light" size="sm">DRAFT</Badge>
                                    {new Date(plan.planDate).toISOString().split('T')[0] < new Date().toISOString().split('T')[0] && (
                                        <Badge color="orange" variant="light" size="sm" leftSection={<IconAlertTriangle size={10} />}>
                                            Outdated
                                        </Badge>
                                    )}
                                </Group>
                            </Group>
                        </Card>
                    ))}
                </Stack>
            )}
        </Stack>
    );
};

export default RebalanceOverview;
